import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import styled from "styled-components";
import { buttonGradient } from "../../../../common/materials/LinearGradient";
import TooltipInfo from "../../../../common/elements/TooltipInfo";
import AlertReadOnlyUser from "../../../../common/elements/AlertReadOnlyUser";
import { addOrRemoveWatchlist } from "../../../../components/mypage/MypageApiService";
import {
  AuctionInvite,
  joinAuction,
} from "../../../../services/ProductsApiService";
import { setToken } from "../../../../services/ApiService";

const StyledWishContainer = styled(Grid)`
  display: flex;
  width: auto;
  justify-content: space-around;
`;

const StyledButton = styled.button<{ disabled?: boolean }>`
  color: ${(props) => props.theme.colors.base.white};
  border: none;
  font-size: 1.3em;
  width: 100%;
  border-radius: 5px;
  background: ${buttonGradient};
  border: 1px solid ${(props) => props.theme.colors.base.white};
  cursor: pointer;

  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    opacity: 0.5;
    background: ${props.theme.colors.base.lightGrey};
    border: 1px solid ${props.theme.colors.base.lightGrey};
    pointer-events: none;
  `}

  &:hover {
    border: 1px solid
      ${(props) => props.theme.colors.base.buttonGradientFirstColor};
  }

  .notAvailable {
    cursor: default;
    background: ${(props) => props.theme.colors.base.lightGrey};
    border: 1px solid ${(props) => props.theme.colors.base.white};
    color: ${(props) => props.theme.colors.base.white};

    &:hover {
      border: 1px solid ${(props) => props.theme.colors.base.lightGrey};
    }
  }
`;

const StyledWatchlist = styled(Grid)`
  padding: 0.5em 0em;
  text-align: center;
  padding: 6px 5px;
`;

const StyledWishlistIcon = styled("img")`
  width: 15px;
  height: 15px;
  vertical-align: top;
  margin-top: 4px;
`;

const StyledWrapper = styled(Grid)`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const StyledTooltipText = styled.div`
  color: ${(props) => props.theme.colors.base.buttonGradientFirstColor};
  font-size: 12px;
  padding: 0 5px;
`;

interface IProps {
  disabled?: boolean;
  productData: any;
  getDetails: (id: string) => void;
  alertType: (
    alertType: string,
    isOpenToast: boolean,
    showMessage: string
  ) => void;
}

const WatchButton = (props: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [openReadOnlyUserToast, setOpenReadOnlyUserToast] = useState(false);
  const [joinedAuctions, setJoinedAuctions] = useState<any[]>([]);
  const [isAuctionUserId, setIsAuctionUserId] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  const { productData, getDetails, disabled } = props;
  const { auctionId, perId, auctionState, watched, sellerAuctionUserId } =
    productData;

  const isReadOnlyUser = JSON.parse(
    localStorage.getItem("isReadOnlyUser") || "false"
  );

  useEffect(() => {
    const fetchJoinedAuctions = async () => {
      try {
        const token = localStorage.getItem("id_token");
        if (token) {
          setToken(token);
        }
        const { data } = await AuctionInvite(auctionId);
        const joinedAuctionsResponse = await joinAuction();
        const results = joinedAuctionsResponse?.results || [];
        setJoinedAuctions(results);

        const isSeller = data.auctionUserId === sellerAuctionUserId;
        setIsAuctionUserId(isSeller);
      } catch (error) {
        console.error("Failed to fetch joined auctions:", error);
      }
    };

    fetchJoinedAuctions();
  }, [sellerAuctionUserId]);

  useEffect(() => {
    const isSeller = joinedAuctions.some(
      (item: any) => item.auctionUserId === sellerAuctionUserId
    );
    setIsAuctionUserId(isSeller);
  }, [perId, joinedAuctions, sellerAuctionUserId]);

  const addToWatchList = async (perId: string, auctionId: string) => {
    const { status } = await addOrRemoveWatchlist(
      `${auctionId}/${perId}/watch`
    );
    if (status === 200) {
      setDisableButton(false);
      props.alertType(
        "success",
        true,
        `${t("trans:addToWatchlistSuccessfully")}`
      );
      getDetails(perId);
    } else {
      props.alertType("error", true, `${t("common:somethingWentWrong")}`);
    }
  };

  const removeFromWatchlist = async (perId: string, auctionId: string) => {
    setDisableButton(true);
    const { status } = await addOrRemoveWatchlist(
      `${auctionId}/${perId}/unwatch`
    );
    if (status === 200) {
      setDisableButton(false);
      props.alertType(
        "success",
        true,
        `${t("trans:removedFromWatchlistSuccessfully")}`
      );
      getDetails(perId);
    } else {
      if (status === 412) {
        setTimeout(() => {
          history.push("/preview");
        }, 2000);
      }
      props.alertType("error", true, `${t("common:somethingWentWrong")}`);
    }
  };

  const isWatched = () => watched === true;

  const isAuctionStateNotStarted = () => true;

  const isButtonClickable = () => {
    return (
      (!isAuctionUserId || sellerAuctionUserId === undefined) &&
      disableButton === false &&
      isReadOnlyUser === false
    );
  };

  const addCallback = (e: any) => {
    e.preventDefault();
    addToWatchList(perId, auctionId);
  };

  const removeCallback = (e: any) => {
    e.preventDefault();
    removeFromWatchlist(perId, auctionId);
  };

  const renderActionButtonTemplate = (
    image: string,
    label: string,
    callback: (e: any) => void
  ) => {
    return (
      <StyledWishContainer onClick={!disabled ? callback : undefined}>
        <StyledWatchlist item xs={12}>
          <StyledWishlistIcon src={`/images/${image}.png`} />{" "}
          {t(`common:preview:${label}`)}
        </StyledWatchlist>
      </StyledWishContainer>
    );
  };

  const renderButtonTemplate = (image: string, label: string) => {
    return (
      <StyledWishContainer
        className="notAvailable"
        onClick={!disabled ? renderReadOnlyUserToast : undefined}
      >
        <StyledWatchlist item xs={9}>
          <StyledWishlistIcon src={`/images/${image}.png`} />{" "}
          {t(`common:preview:${label}`)}
        </StyledWatchlist>
      </StyledWishContainer>
    );
  };

  const renderReadOnlyUserToast = () => {
    if (isReadOnlyUser || isAuctionUserId) {
      setOpenReadOnlyUserToast(true);
    } else {
      setOpenReadOnlyUserToast(false);
    }

    setTimeout(() => {
      setOpenReadOnlyUserToast(false);
    }, 3000);
  };

  const renderWatchedButton = () => {
    return isAuctionStateNotStarted()
      ? isButtonClickable()
        ? renderActionButtonTemplate(
            "heart-grey",
            "addedToWatchList",
            addCallback
          )
        : renderButtonTemplate("heart-grey", "addedToWatchList")
      : renderButtonTemplate("heart-grey", "addedToWatchList");
  };

  const renderUnWatchedButton = () => {
    return isAuctionStateNotStarted()
      ? isButtonClickable()
        ? renderActionButtonTemplate("heart", "addWatchList", removeCallback)
        : renderButtonTemplate("heart", "addWatchList")
      : renderButtonTemplate("heart", "addWatchList");
  };

  return (
    <TooltipInfo
      message={t("common:preview:productDetail:watchTooltip")}
      iconClassName="technicalGlitchAuction"
    >
      <StyledTooltipText>
        <StyledWrapper container spacing={0}>
          <StyledButton disabled={disabled}>
            {isWatched() ? renderUnWatchedButton() : renderWatchedButton()}
          </StyledButton>
        </StyledWrapper>
      </StyledTooltipText>

      {openReadOnlyUserToast && (
        <AlertReadOnlyUser
          isReadOnlyUser={isReadOnlyUser}
          isSeller={isAuctionUserId}
        />
      )}
    </TooltipInfo>
  );
};

export default WatchButton;
